import { FetchStrategy } from "@betomorrow/micro-stores"
import SlotEmptySVG from "@images/empty/slot_empty.svg"
import { PlusIcon } from "@images/svgIcons/plusIcon"
import { BoxSlotSession } from "@modules/boxSlot/boxSlotTypes"
import {
  BoxSessionAddSubscriberModal,
  BoxSessionAddSubscriberModalRef,
} from "@modules/boxSlot/components/boxSessionAddSubscriberModal"
import BoxSessionSubscriber from "@modules/boxSlot/components/boxSessionSubscriber"
import { useBoxSlotPendingSubscribers, useBoxSlotSubscribers } from "@modules/boxSlot/hooks/useBoxSlotDetails"
import { word } from "@modules/core/utils/i18n"
import { ParticipantStatus, Subscriber } from "@modules/profile/subscriber/subscriberTypes"
import { HUButton } from "@modules/ui/components/huButton"
import { HUText } from "@modules/ui/components/huText"
import Line from "@modules/ui/components/line"
import { Skeleton } from "primereact/skeleton"
import { useEffect, useMemo, useRef, useState } from "react"
import InfiniteScroll from "react-infinite-scroll-component"
import { DefaultTheme, useTheme } from "styled-components"

export const BoxSlotRegistrationInfo: React.FC<{
  boxSlot: BoxSlotSession
}> = ({ boxSlot }) => {
  const [fetched, setFetched] = useState(false)

  const theme = useTheme()
  const {
    result: subscribers,
    list: listSubscribers,
    loading: loadingSubscribers,
    listMore: listMoreSubscribers,
    totalSize: totalSubscriberSize,
  } = useBoxSlotSubscribers(boxSlot.id, FetchStrategy.Never)
  const {
    result: pendingSubscribers,
    list: listPendingSubscribers,
    loading: loadingPendingSubscribers,
    listMore: listMorePendingSubscribers,
    totalSize: totalPendingSize,
  } = useBoxSlotPendingSubscribers(boxSlot.id, FetchStrategy.Never)

  useEffect(() => {
    Promise.all([listPendingSubscribers(), listSubscribers()]).then(() => setTimeout(() => setFetched(true), 200))
  }, [])

  const addSubscriberModalRef = useRef<BoxSessionAddSubscriberModalRef>(null)

  const renderTotalSubscribers = useMemo(() => {
    const totalPending = totalPendingSize ?? 0
    const totalSubscribers = totalSubscriberSize ?? 0

    return `${totalPending + totalSubscribers} / ${boxSlot.participantLimit} ${word(
      "box.slotTemplateGroup.form.participantLimit.unit",
      {
        count: totalPending + totalSubscribers,
      },
    )}`
  }, [totalPendingSize, totalSubscriberSize])

  const listMore = () => {
    if (subscribers.length < (totalSubscriberSize || 0)) {
      listMoreSubscribers()
    }
    if (pendingSubscribers.length < (totalPendingSize || 0)) {
      listMorePendingSubscribers()
    }
  }

  return (
    <div className="flex flex-column" id="a">
      <HUText className="mt-2" fontStyle="BS" color={theme.neutralColor400}>
        {renderTotalSubscribers}
      </HUText>
      <div className="flex gap-4 ml-1 mt-4 mb-3">
        <HUButton
          className=""
          type="Rounded"
          size="M"
          colorType="Secondary"
          icon={{ iconView: <PlusIcon color={theme.neutralColor700} width={14} height={14} /> }}
          onClick={() => addSubscriberModalRef.current?.open()}
        />
        <HUText className="text-center align-content-center ml-1" fontStyle="LL" color={theme.mainWhite}>
          {word("boxSlotRegistrationInfo.button.inviteAthlete")}
        </HUText>
      </div>
      <Line color={`${theme.neutralColor400}`} strokeWidth={1} className="m-0 mb-2" />

      {!fetched ? (
        <>
          {Array.from({ length: 10 }).map((_, index) => (
            <Skeleton key={index} height="4rem" className="rounded-full mt-3" />
          ))}
        </>
      ) : subscribers.length === 0 &&
        pendingSubscribers.length === 0 &&
        !loadingSubscribers &&
        !loadingPendingSubscribers ? (
        emptyTemplate(theme as DefaultTheme)
      ) : (
        <InfiniteScroll
          dataLength={subscribers.length + pendingSubscribers.length}
          next={listMore}
          hasMore={
            subscribers.length < (totalSubscriberSize || 0) || pendingSubscribers.length < (totalPendingSize || 0)
          }
          scrollableTarget={"sidebar-content"}
          loader={<HUText fontStyle="BM">{word("global.loading")}</HUText>}
          style={{ paddingBottom: 80 }}
        >
          {slotSubscriberTemplate(theme as DefaultTheme, subscribers, boxSlot, "registered")}
          {pendingSubscribers.length > 0
            ? slotSubscriberTemplate(theme as DefaultTheme, pendingSubscribers, boxSlot, "pending")
            : null}
        </InfiniteScroll>
      )}

      <BoxSessionAddSubscriberModal ref={addSubscriberModalRef} slotId={boxSlot.id} />
    </div>
  )
}

const slotSubscriberTemplate = (
  theme: DefaultTheme,
  slotSubscribers: readonly Subscriber[],
  boxSlot: BoxSlotSession,
  participantStatus: ParticipantStatus,
) => {
  return (
    <div className={`${participantStatus === "pending" ? "" : "mb-5"}`}>
      {participantStatus === "pending" ? (
        <div>
          <Line color={`${theme.mainWhite}`} strokeWidth={1} className="m-0 mb-2" />
          <HUText fontStyle="BS" color={theme.neutralColor600}>
            {word("boxSlotRegistrationInfo.slotSubscriberTemplate.waitingList")}
          </HUText>
        </div>
      ) : null}
      <div className={`${participantStatus === "pending" ? "mt-3" : ""}`}>
        {slotSubscribers.map((subscriber) => (
          <BoxSessionSubscriber
            slotId={boxSlot.id}
            boxSlot={boxSlot}
            subscriber={subscriber}
            participantStatus={participantStatus}
            key={subscriber.id}
          />
        ))}
      </div>
      {participantStatus === "pending" ? (
        <Line color={`${theme.mainWhite}`} strokeWidth={1} className="m-0 mt-5" />
      ) : null}
    </div>
  )
}

const emptyTemplate = (theme: DefaultTheme) => {
  return (
    <div className="flex flex-column h-15rem mt-7">
      <div className=" justify-content-center">
        <HUText fontStyle="TL" color={theme.neutralColor700} className="flex text-left w-22rem mx-auto">
          {word("boxSlotRegistrationInfo.emptyTemplate.title")}
        </HUText>
        <HUText fontStyle="BM" color={theme.neutralColor700} className="flex text-left mt-3 w-22rem mx-auto">
          {word("boxSlotRegistrationInfo.emptyTemplate.subtitle")}
        </HUText>
      </div>

      <div className="flex flex-1 justify-content-center text-center align-items-center mt-2">
        <img src={SlotEmptySVG} alt="SlotEmptySVG" width={300} />
      </div>
    </div>
  )
}
